import { Suspense, lazy, memo } from 'react';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { ModelCategoryV1 } from 'pages/ModelOverviewPage/blockv1/types';
import { Flags } from 'models/Global.interface';
import BlockList from 'pages/ModelOverviewPage/block';
import { isDashboardRevampEnabled } from 'utils/dashboardRevampFlag';

import { TabType } from '../types';
import { HEADER_CONFIG } from '../constants';

import { BlocksTabModule } from './blocksTabModule';

// Lazy load heavy components
const Dashboard = lazy(() => import('pages/ModelOverviewPage/Dashboard-v2'));
// This branch's real Dashboard redesign — Dashboard-v2 above stays the
// untouched original fallback.
const DashboardRevamp = lazy(() => import('pages/ModelOverviewPage/DashboardRevamp'));
// Dimensions tab stays on this branch's original module — DimensionsRevamp
// was brought over for later but isn't wired in here.
const Dimensions = lazy(() => import('pages/ModelOverviewPage/dimensions'));
const DataImport = lazy(() => import('pages/ModelOverviewPage/Import'));

interface TabContentProperties {
  activeTab: TabType;
  blocks?: any[];
  categories?: ModelCategoryV1[];
  flags?: Flags;
}

const LoadingFallback = () => (
  <Center h='200px'>
    <Spinner size='xl' color='#745BE3' thickness='4px' />
  </Center>
);

const TabContent = memo(({ activeTab, blocks, categories, flags }: TabContentProperties) => {
  // const isTablet = Boolean((useMediaQuery('(max-width: 768px)') as any)[0]);

  const blocksManagementOn = Boolean(flags?.blocksManagement);
  const isDashboardRevamp = isDashboardRevampEnabled(flags);

  return (
    <Box
      // Derived from the header's own constant rather than a copy of its
      // number. The two had already drifted — the header declared 53px but,
      // inside its `zoom: 0.9` box, painted 47.7px, so this subtracted ~5.3px
      // too much from every page in the app.
      height={`calc(100vh - ${HEADER_CONFIG.HEIGHT})`}
      overflow='auto'
      bg={
        (blocksManagementOn && activeTab === 'blocks') ||
        (activeTab === 'dashboard' && isDashboardRevamp)
          ? 'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)'
          : 'gradient.body'
      }
      px={
        (blocksManagementOn && activeTab === 'blocks') ||
        (activeTab === 'dashboard' && isDashboardRevamp)
          ? 0
          : 4
      }
      pt={
        (blocksManagementOn && activeTab === 'blocks') ||
        (activeTab === 'dashboard' && isDashboardRevamp)
          ? 0
          : 4
      }
    >
      {/* <Box px={isTablet ? 4 : 7} pt={3}>
        <BreadCrumbComponent hasBlock={false} />
      </Box> */}
      {/* The revamped header adds a 10px lead-in above this row; the legacy
          one doesn't. Both offsets now hang off HEADER_CONFIG.HEIGHT so they
          can't drift from the real header height again. */}
      <Box
        height={`calc(100vh - ${
          flags?.uiRevamp ? `(${HEADER_CONFIG.HEIGHT} + 10px)` : HEADER_CONFIG.HEIGHT
        })`}
      >
        <Suspense fallback={<LoadingFallback />}>
          {activeTab === 'dashboard' &&
            (isDashboardRevampEnabled(flags) ? <DashboardRevamp /> : <Dashboard />)}
          {activeTab === 'blocks' &&
            (blocksManagementOn ? (
              <BlocksTabModule blocks={blocks || []} categories={categories || []} />
            ) : (
              <BlockList blocks={blocks || []} />
            ))}
          {activeTab === 'dimensions' && <Dimensions />}
          {activeTab === 'import' && <DataImport />}
        </Suspense>
      </Box>
    </Box>
  );
});

TabContent.displayName = 'TabContent';

export default withLDConsumer()(TabContent);
